import {
  createRootRoute,
  Outlet,
  Link,
  redirect,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RoleProvider } from "@/lib/roles";
import { InventoryFilterProvider } from "@/lib/inventory-filter";
import { inventorySearchSchema } from "@/lib/inventory-scope-codec";
import { ThemeProvider } from "@/lib/theme";
import { getSession, refresh } from "@/lib/auth";
import { useBuildingEventStream } from "@/lib/useBuildingEventStream";

const queryClient = new QueryClient();

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          Sivua ei löydy
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Sivu on poistettu tai siirretty.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Etusivulle
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  // Inventory scope (building/group/rajaus) is sticky in the URL search params for every
  // route — see issue #536. Declared once here since search schemas are inherited by children.
  validateSearch: (search) => inventorySearchSchema.parse(search),
  beforeLoad: async ({ location }) => {
    if (location.pathname === "/login") return;
    // In-memory access token is gone after a hard reload — try one silent refresh via the
    // httpOnly cookie before giving up, so a reload keeps an authenticated user logged in.
    if (!getSession() && !(await refresh())) {
      throw redirect({ to: "/login" });
    }
  },
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

// Opens the change stream of the building in scope, so open views refresh when it changes.
function BuildingEventStream() {
  useBuildingEventStream();
  return null;
}

function RootComponent() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <RoleProvider>
          <InventoryFilterProvider>
            <BuildingEventStream />
            <Outlet />
          </InventoryFilterProvider>
        </RoleProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
